import { ChangeEvent, KeyboardEvent, useEffect, useState } from "react";
import { ExtendPadding, ExtendPreviewProps } from "../../../types/imageStudio/images";
import { SengiColors } from "../../../utils/SengiColors";

type PaddingSide = keyof ExtendPadding;

export const ExpandPaddingPreview = ({
    padding,
    targetWidth,
    targetHeight,
    onChangePadding
}: ExtendPreviewProps) => {
    const maxPreviewWidth = 150;
    const maxPreviewHeight = 90;
    const labelSpace = 42;

    const [draft, setDraft] = useState<Record<PaddingSide, string>>({
        top: String(padding.top ?? 0),
        right: String(padding.right ?? 0),
        bottom: String(padding.bottom ?? 0),
        left: String(padding.left ?? 0)
    });

    useEffect(() => {
        setDraft({
            top: String(padding.top ?? 0),
            right: String(padding.right ?? 0),
            bottom: String(padding.bottom ?? 0),
            left: String(padding.left ?? 0)
        });
    }, [padding.top, padding.right, padding.bottom, padding.left]);

    const fullW = Math.max(1, targetWidth + padding.left + padding.right);
    const fullH = Math.max(1, targetHeight + padding.top + padding.bottom);

    const scale = Math.min(
        maxPreviewWidth / fullW,
        maxPreviewHeight / fullH
    );

    const baseW = targetWidth * scale;
    const baseH = targetHeight * scale;
    const leftW = padding.left * scale;
    const rightW = padding.right * scale;
    const topH = padding.top * scale;
    const bottomH = padding.bottom * scale;

    const previewW = baseW + leftW + rightW;
    const previewH = baseH + topH + bottomH;

    const fill = "rgba(147,129,255,0.32)";
    const border = `1px dashed ${SengiColors.sng_lavendel}`;

    const parseDraftValue = (value: string) => {
        if (value.trim() === "") return 0;

        const parsed = Math.round(Number(value));
        if (!Number.isFinite(parsed)) return 0;

        return Math.max(0, parsed);
    };

    const commitSide = (side: PaddingSide) => {
        const value = parseDraftValue(draft[side]);

        setDraft(prev => ({
            ...prev,
            [side]: String(value)
        }));

        onChangePadding({
            ...padding,
            [side]: value
        });
    };

    const commitAll = () => {
        const next: ExtendPadding = {
            top: parseDraftValue(draft.top),
            right: parseDraftValue(draft.right),
            bottom: parseDraftValue(draft.bottom),
            left: parseDraftValue(draft.left)
        };

        setDraft({
            top: String(next.top),
            right: String(next.right),
            bottom: String(next.bottom),
            left: String(next.left)
        });

        onChangePadding(next);
    };

    const handleInputKeyDown = (
        event: KeyboardEvent<HTMLInputElement>,
        side: PaddingSide
    ) => {
        if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            commitSide(side);
            event.currentTarget.blur();
        }

        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();

            setDraft(prev => ({
                ...prev,
                [side]: String(padding[side] ?? 0)
            }));

            event.currentTarget.blur();
        }
    };

    const inputStyle: React.CSSProperties = {
        width: 50,
        height: 22,
        padding: 0,
        margin: 0,
        border: "none",
        outline: "none",
        background: "transparent",
        color: "#fff",
        textAlign: "center",
        pointerEvents: "auto",
        position: "relative",
        zIndex: 5,
        appearance: "textfield",
        MozAppearance: "textfield"
    };

    const renderInput = (side: PaddingSide) => (
        <input
            type="number"
            step={1}
            min={0}
            value={draft[side]}
            placeholder="0"
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setDraft(prev => ({
                    ...prev,
                    [side]: event.target.value
                }));
            }}
            onBlur={() => commitSide(side)}
            onKeyDown={(event) => handleInputKeyDown(event, side)}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            style={inputStyle}
        />
    );

    return (
        <div
            className="mt-2 text-light small"
            style={{
                pointerEvents: "none",
                userSelect: "none"
            }}
        >
            <div
                className="mx-auto position-relative"
                style={{
                    width: previewW + labelSpace * 2,
                    height: previewH + labelSpace * 2,
                    minWidth: 150,
                    minHeight: 110,
                    overflow: "visible"
                }}
            >
                <div
                    className="position-absolute d-flex justify-content-center align-items-center"
                    style={{
                        top: 0,
                        left: labelSpace,
                        width: previewW,
                        height: labelSpace
                    }}
                >
                    {renderInput("top")}
                </div>

                <div
                    className="position-absolute d-flex justify-content-center align-items-center"
                    style={{
                        bottom: 0,
                        left: labelSpace,
                        width: previewW,
                        height: labelSpace
                    }}
                >
                    {renderInput("bottom")}
                </div>

                <div
                    className="position-absolute d-flex justify-content-center align-items-center"
                    style={{
                        left: 0,
                        top: labelSpace,
                        width: labelSpace,
                        height: previewH
                    }}
                >
                    {renderInput("left")}
                </div>

                <div
                    className="position-absolute d-flex justify-content-center align-items-center"
                    style={{
                        right: 0,
                        top: labelSpace,
                        width: labelSpace,
                        height: previewH
                    }}
                >
                    {renderInput("right")}
                </div>

                <div
                    className="position-absolute"
                    style={{
                        left: labelSpace,
                        top: labelSpace,
                        width: previewW,
                        height: previewH,
                        border,
                        background: fill,
                        boxSizing: "border-box",
                        pointerEvents: "none"
                    }}
                />

                <div
                    className="position-absolute"
                    style={{
                        left: labelSpace + leftW,
                        top: labelSpace + topH,
                        width: baseW,
                        height: baseH,
                        border: `1px solid ${SengiColors.sng_lavendel}`,
                        background: "rgba(255,255,255,0.18)",
                        boxSizing: "border-box",
                        pointerEvents: "none"
                    }}
                />
            </div>
        </div>
    );
};